// Owner: typed-values (wave 1), views-grid (wave 2). The records query surface: filter, sort, keyset
// cursors and offsets, full-text search, counts, totals, groups (date buckets, one group, the first N
// records of every group with a cursor per group) and conditional rule matches.
import { z } from 'zod';
import { FieldPath, FilterNodeSchema, SortSpec } from './filter';
import { RecordDTO } from './records';

export const AggregateFn = z.enum([
  'count',
  'count_empty',
  'count_filled',
  'sum',
  'avg',
  'min',
  'max',
  // views-grid: generic aggregates every field (or every numeric field) supports.
  'median',
  'count_unique',
  'percent_filled',
]);
export type AggregateFn = z.infer<typeof AggregateFn>;

export const AggregateSpec = z
  .object({
    /** Omit for `count`. */
    field: FieldPath.optional(),
    fn: AggregateFn,
    /**
     * Numeric field (e.g. probability) that weights `sum` / `avg` of `field`: weighted pipeline
     * = sum(amount × probability). Only for `sum` and `avg`.
     */
    weightBy: FieldPath.optional(),
    /** Result key; defaults to `<fn>_<field>` or `count`. */
    as: z
      .string()
      .regex(/^[a-z][a-z0-9_]*$/)
      .optional(),
  })
  .strict();
export type AggregateSpec = z.infer<typeof AggregateSpec>;

export const CountMode = z.enum(['none', 'estimate', 'exact']);
export type CountMode = z.infer<typeof CountMode>;

/** Date bucket of a date or date-time `groupBy` (group keys are the bucket's first day, `YYYY-MM-DD`). */
export const QueryGroupBucket = z.enum(['day', 'week', 'month', 'quarter', 'year']);
export type QueryGroupBucket = z.infer<typeof QueryGroupBucket>;

/** A filter to test the returned records against (conditional rules); matches come back by id. */
export const RuleProbe = z
  .object({
    id: z
      .string()
      .min(1)
      .max(40)
      .regex(/^[A-Za-z0-9_-]+$/),
    filter: FilterNodeSchema,
  })
  .strict();
export type RuleProbe = z.infer<typeof RuleProbe>;

/** Largest offset a query may skip (deep jumps in a virtualised grid). */
export const QUERY_MAX_OFFSET = 10_000_000;

/** Body of POST /objects/:key/query. */
export const QueryRequest = z
  .object({
    filter: FilterNodeSchema.nullish(),
    sort: z.array(SortSpec).max(5).default([]),
    /** Field api keys to return (default: all). */
    fields: z.array(z.string()).max(200).optional(),
    limit: z.number().int().min(1).max(500).default(50),
    cursor: z.string().max(4000).nullish(),
    /**
     * Skip this many records (random access: a grid jumping to row 50,000). Not with `cursor`, which
     * is cheaper for the next page.
     */
    offset: z.number().int().min(0).max(QUERY_MAX_OFFSET).optional(),
    /** Full text search over the record's searchable text. */
    q: z.string().max(200).optional(),
    count: CountMode.default('estimate'),
    /** Group key (field api key of a groupable field) for grouped aggregates. */
    groupBy: FieldPath.optional(),
    /** Bucket of a date or date-time `groupBy`. */
    groupBucket: QueryGroupBucket.optional(),
    /**
     * Only the records of this group of `groupBy` (`null` = the group without a value). Group keys
     * are those of `groups[].key` (as text).
     */
    groupKey: z.string().max(200).nullable().optional(),
    /**
     * With `groupBy`: `records` holds the first N records of every group (in `sort` order) and each
     * group lists its `ids` and the `nextCursor` to continue it (with `groupKey`). Grouped tables and
     * boards load in one request.
     */
    perGroupLimit: z.number().int().min(1).max(200).optional(),
    aggregates: z.array(AggregateSpec).max(40).default([]),
    /** Conditional rules to evaluate for the returned records (`matches`). */
    rules: z.array(RuleProbe).max(30).default([]),
    /** Include `sources` provenance per field in the records. */
    includeSources: z.boolean().default(false),
    /**
     * Order by the manual card order of this saved view first (`view_record_rank`, views-layouts):
     * ranked records in rank order, then the others in `sort` order. Boards in manual order pass it.
     */
    rankedBy: z.uuid().optional(),
  })
  // Cross-field rules (cursor xor offset; groupKey, groupBucket and perGroupLimit need groupBy) are
  // checked by the query compiler: a refinement here would break `.omit()` / `.extend()` of callers.
  .strict();
export type QueryRequest = z.input<typeof QueryRequest>;
export type ParsedQueryRequest = z.output<typeof QueryRequest>;

export const QueryCount = z.object({ value: z.number().int(), estimated: z.boolean() });
export type QueryCount = z.infer<typeof QueryCount>;

export const QueryGroup = z.object({
  key: z.unknown(),
  count: z.number().int(),
  aggregates: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
  /** With `perGroupLimit`: ids of this group's records in `records`, in order. */
  ids: z.array(z.string()).optional(),
  /** With `perGroupLimit`: cursor of the group's next records (pass with `groupKey`). */
  nextCursor: z.string().nullable().optional(),
});
export type QueryGroup = z.infer<typeof QueryGroup>;

export const QueryResponse = z.object({
  records: z.array(RecordDTO),
  nextCursor: z.string().nullable(),
  count: QueryCount.nullable(),
  /** Aggregates over the whole filtered set (not only the page). */
  totals: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
  /** Present when `groupBy` is set. */
  groups: z.array(QueryGroup).optional(),
  /** With `rules`: the rule ids each returned record matches, in rule order (records without a match left out). */
  matches: z.record(z.string(), z.array(z.string())).optional(),
  /** With `rules`: rules that could not be applied (they name a field or relation that is gone); the records load without them. */
  ruleIssues: z.array(z.object({ id: z.string(), message: z.string() })).optional(),
});
export type QueryResponse = z.infer<typeof QueryResponse>;
